import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { Loader2, CheckCircle2, RotateCcw, X } from "lucide-react";
import axios from "axios";
import { API } from "../../contexts/AuthContext";
import { toast } from "sonner";
import SelectorEmpleados from "./SelectorEmpleados";
import { useTurnos, filtrarEmpleados, idsACargar, SelectTurno } from "./FiltroTurnoObra";   // Fix FC
import SelectBuscable from "../../components/SelectBuscable";   // Fix FT (2026-10-12)
import IngresosObras, { useIngresosObras, pctPorPto, pctFmt } from "./IngresosObras";   // Fix GD (2026-10-14)

const inputCls = "w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4] bg-white";
const labelCls = "block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1";

function ReopenMotivoModal({ open, onClose, onConfirm, loading }) {
  const [motivo, setMotivo] = useState("");
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="obra-reopen-motivo-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" data-testid="obra-reopen-motivo-modal">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-base font-bold text-gray-900">Reabrir Parte Diario Obra — Motivo</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-3">
          <textarea value={motivo} onChange={e => setMotivo(e.target.value)} rows={3} placeholder="Ej: presupuesto/ítem asignado incorrectamente..." className={`${inputCls} text-sm`} data-testid="obra-reopen-motivo-input" />
          <button onClick={() => onConfirm(motivo)} disabled={loading || !motivo.trim()} className="w-full bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded-xl py-2.5 font-bold text-sm flex items-center justify-center gap-2" data-testid="obra-reopen-motivo-confirm-btn">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <RotateCcw size={16} />} Confirmar Reapertura
          </button>
        </div>
      </div>
    </div>, document.body
  );
}

export default function ParteDiarioObraTab({ projects, workers }) {
  const activeProjects = (projects || []).filter(p => p.status === "activo");
  const [projectId, setProjectId] = useState("");
  // Fix DW (2026-09-23): "Lo mismo en el parte diario obra" (Sergio, 23/09) — el
  // campo de texto libre pasa a ser el mismo desplegable con multicheck.
  const [workerIds, setWorkerIds] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [records, setRecords] = useState([]);
  const [budgets, setBudgets] = useState([]);
  // Fix FT (2026-10-12): se quita Frente. Ítem y Tarea se resuelven por presupuesto:
  // los ítems de cada Pto elegido y las tareas de la obra de ese Pto, cargados una vez.
  const [itemsPorPto, setItemsPorPto] = useState({});
  const [obraPorProyecto, setObraPorProyecto] = useState({});
  const yaPedidos = useRef({ ptos: new Set(), proyectos: new Set() });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const turnos = useTurnos();   // Fix FC (2026-10-06): filtro por turno
  const [turnoId, setTurnoId] = useState("");

  // Fix FT (2026-10-12): "En Parte Diario Obra, no filtran los presupuestos" (Sergio). Sin obra
  // elegida el desplegable de Pto quedaba vacío; ahora lista todos los aprobados (modo liviano,
  // sin ítems) y con una obra elegida, los de esa obra.
  useEffect(() => {
    axios.get(`${API}/budgets`, { params: { project_id: projectId || undefined, status: "aprobado", light: true }, withCredentials: true })
      .then(r => setBudgets(r.data || [])).catch(() => setBudgets([]));
  }, [projectId]);

  // Fix GD (2026-10-14): "esa misma columna de referencia [% de ingresos por obra] debería estar cuando se
  // valida la imputación de los costos directos a los centros de costos" (CEN). Se ve arriba y en cada Pto.
  const ingresos = useIngresosObras(dateFrom, dateTo);
  const pctPto = useMemo(() => pctPorPto(ingresos), [ingresos]);
  const ptoOptions = useMemo(
    () => budgets.map(b => {
      const pct = pctPto[b._id || b.id];
      return { value: b._id || b.id, label: `${b.number} · ${b.nombre || b.obra}${pct !== undefined ? ` · ${pctFmt(pct)} % ingresos` : ""}` };
    }),
    [budgets, pctPto]
  );
  // Obra (projects._id) a la que pertenece un Pto; sin dato, la que está elegida arriba.
  const proyectoDe = useCallback((pid) => {
    const b = pid ? budgets.find(x => (x._id || x.id) === pid) : null;
    return (b && b.project_id) || projectId || "";
  }, [budgets, projectId]);

  // Ítems de cada Pto que aparece en las filas (una sola vez por Pto).
  useEffect(() => {
    const ptos = new Set();
    records.forEach(r => { [1, 2].forEach(n => { if (r[`tramo${n}_presupuesto_id`]) ptos.add(r[`tramo${n}_presupuesto_id`]); }); });
    ptos.forEach(pid => {
      if (yaPedidos.current.ptos.has(pid)) return;
      yaPedidos.current.ptos.add(pid);
      axios.get(`${API}/budgets/${pid}/items-para-pedido`, { withCredentials: true })
        .then(r => setItemsPorPto(prev => ({ ...prev, [pid]: { items: r.data?.items || [], niveles: r.data?.niveles || [] } })))
        .catch(() => setItemsPorPto(prev => ({ ...prev, [pid]: { items: [], niveles: [] } })));
    });
  }, [records]);

  // Obra gemela de cada proyecto y sus tareas (CEN 2.0 FASE 4: el jornal se imputa a obra_tareas).
  useEffect(() => {
    const proyectos = new Set();
    if (projectId) proyectos.add(projectId);
    records.forEach(r => { [1, 2].forEach(n => { const p = r[`tramo${n}_presupuesto_id`] ? proyectoDe(r[`tramo${n}_presupuesto_id`]) : ""; if (p) proyectos.add(p); }); });
    proyectos.forEach(proy => {
      if (yaPedidos.current.proyectos.has(proy)) return;
      yaPedidos.current.proyectos.add(proy);
      const guardar = (o, tareas) => setObraPorProyecto(prev => ({ ...prev, [proy]: { obraId: o, tareas } }));
      axios.get(`${API}/obras/by-project/${proy}`, { withCredentials: true })
        .then(r => {
          const o = r.data?.obra;
          if (!o?.id) { guardar("", []); return; }
          axios.get(`${API}/obras/${o.id}/tareas`, { withCredentials: true })
            .then(tr => guardar(o.id, tr.data?.tareas || []))
            .catch(() => guardar(o.id, []));
        })
        .catch(() => guardar("", []));
    });
  }, [records, projectId, proyectoDe]);

  const loadRecords = useCallback(async () => {
    if (!dateFrom || !dateTo) { toast.error("Definí el rango de fechas del período habilitado"); return; }
    // Fix FC: con turno, se cargan los del turno (o los elegidos de ese turno).
    const ids = turnoId ? idsACargar(workers, workerIds, turnoId, projectId) : workerIds;
    if (turnoId && !ids.length) { toast.info("No hay empleados en ese turno con el filtro elegido."); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/hr/parte-diario/obra/records`, {
        params: { project_id: projectId || undefined, worker_ids: ids.length ? ids.join(",") : undefined, date_from: dateFrom, date_to: dateTo },
        withCredentials: true,
      });
      // Fix FT: cada carga vuelve a pedir ítems y tareas, por si cambiaron desde la última vez.
      yaPedidos.current = { ptos: new Set(), proyectos: new Set() };
      setItemsPorPto({}); setObraPorProyecto({});
      setRecords(res.data);
      setSelected([]);
      // Fix DX (2026-09-23): una carga vacía no mostraba nada; ahora dice por qué.
      if (!res.data.length) toast.info("No hay días validados en el Parte Diario Adm. para ese período y empleado(s).");
    } catch (err) { toast.error(err.response?.data?.detail || "Error al cargar el período"); }
    finally { setLoading(false); }
  }, [projectId, workerIds, turnoId, workers, dateFrom, dateTo]);

  const updateLocal = (idx, field, value) => setRecords(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  const updateLocalMany = (idx, cambios) => setRecords(prev => prev.map((r, i) => i === idx ? { ...r, ...cambios } : r));   // Fix FT

  const saveAssign = async (idx) => {
    const r = records[idx];
    setBusy(true);
    // Fix FT: la obra de cada tramo es la del Pto elegido (antes, una sola para toda la tabla).
    const obraDe = (n) => (obraPorProyecto[proyectoDe(r[`tramo${n}_presupuesto_id`])] || {}).obraId || r[`tramo${n}_obra_id`] || "";
    try {
      const res = await axios.put(`${API}/hr/parte-diario/obra/records/${r.id}/assign`, {
        tramo1_presupuesto_id: r.tramo1_presupuesto_id, tramo1_frente: r.tramo1_frente,
        tramo2_presupuesto_id: r.tramo2_presupuesto_id, tramo2_frente: r.tramo2_frente,
        tramo1_obra_id: r.tramo1_item_uid ? obraDe(1) : "", tramo1_item_uid: r.tramo1_item_uid, tramo1_tarea_numero: r.tramo1_tarea_numero || null,
        tramo2_obra_id: r.tramo2_item_uid ? obraDe(2) : "", tramo2_item_uid: r.tramo2_item_uid, tramo2_tarea_numero: r.tramo2_tarea_numero || null,
      }, { withCredentials: true });
      setRecords(prev => prev.map((x, i) => i === idx ? res.data : x));
      toast.success("Asignación guardada");
    } catch (err) { toast.error(err.response?.data?.detail || "Error al asignar"); }
    finally { setBusy(false); }
  };

  const toggleSelect = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleSelectAll = () => setSelected(selected.length === records.length ? [] : records.map(r => r.id));

  const handleValidate = async () => {
    if (!selected.length) { toast.error("Seleccioná al menos un día"); return; }
    setBusy(true);
    try {
      const res = await axios.put(`${API}/hr/parte-diario/obra/validate`, { ids: selected }, { withCredentials: true });
      toast.success(`${res.data.validated_count} día(s) validado(s) — listos para liquidar`);
      loadRecords();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al validar"); }
    finally { setBusy(false); }
  };

  const handleReopen = async (motivo) => {
    setBusy(true);
    try {
      const res = await axios.put(`${API}/hr/parte-diario/obra/reopen`, { ids: selected, motivo }, { withCredentials: true });
      toast.success(`${res.data.reopened_count} día(s) reabierto(s)`);
      setReopenOpen(false);
      loadRecords();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al reabrir"); }
    finally { setBusy(false); }
  };

  // Fix FT (2026-10-12): "los filtros deben ser conforme escritura (…) en la columna tarea debe
  // desplegar las tareas asignadas al ítem escogido como lo hace en Pedido Materiales/Servicios"
  // (Sergio). Pto → Ítem → Tarea, los tres con buscador. El Ítem lista los del Pto elegido que
  // tienen tareas en la obra (si ninguno las tiene, todos); la Tarea, solo las de ese ítem.
  const celdasAsignacion = (r, idx, n, locked) => {
    const pid = r[`tramo${n}_presupuesto_id`] || "";
    const uid = r[`tramo${n}_item_uid`] || "";
    const num = r[`tramo${n}_tarea_numero`] || "";
    const datos = itemsPorPto[pid] || { items: [], niveles: [] };
    const tareasObra = (obraPorProyecto[proyectoDe(pid)] || {}).tareas || [];
    const nivelDe = {};
    datos.niveles.forEach(nv => { nivelDe[nv.id] = nv.label; });
    const uidsConTareas = new Set(tareasObra.map(t => t.item_uid));
    const itemsConTareas = datos.items.filter(bi => uidsConTareas.has(bi.item_uid));
    const itemOptions = (itemsConTareas.length ? itemsConTareas : datos.items).map(bi => ({
      value: bi.item_uid,
      label: `${bi.numero ? bi.numero + " " : ""}${bi.descripcion}${bi.nivel_id && nivelDe[bi.nivel_id] ? " · " + nivelDe[bi.nivel_id] : ""}`,
      grupo: `${bi.seccion_numero ? bi.seccion_numero + " " : ""}${bi.seccion || ""}`.trim() || undefined,
    }));
    const tareaDelItem = tareasObra.filter(t => t.item_uid === uid);
    const deLaTarea = tareaDelItem[0];
    return (
      <>
        <td className="px-2 py-2">
          <SelectBuscable disabled={locked} value={pid} vacio="— Sin asignar —"
            etiquetaActual={r[`tramo${n}_presupuesto_codigo`] || ""}
            options={ptoOptions} placeholderBuscar="Buscar presupuesto..."
            onChange={v => updateLocalMany(idx, { [`tramo${n}_presupuesto_id`]: v, [`tramo${n}_item_uid`]: "", [`tramo${n}_tarea_numero`]: null })}
            className="w-32 border border-gray-300 rounded-lg px-1 py-1 text-xs bg-white" testId={`obra-parte-pto${n}-${idx}`} />
        </td>
        <td className="px-2 py-2">
          <SelectBuscable disabled={locked || !pid} value={uid} vacio={pid ? "— Ítem —" : "— Elegí el Pto —"}
            etiquetaActual={deLaTarea ? `${deLaTarea.item_numero} ${deLaTarea.item_descripcion || ""}`.trim() : "…"}
            options={itemOptions} placeholderBuscar="Buscar ítem..."
            onChange={v => updateLocalMany(idx, { [`tramo${n}_item_uid`]: v, [`tramo${n}_tarea_numero`]: null })}
            className="w-40 border border-gray-300 rounded-lg px-1 py-1 text-xs bg-white" testId={`obra-parte-item${n}-${idx}`} />
        </td>
        <td className="px-2 py-2">
          <SelectBuscable disabled={locked || !uid || !tareaDelItem.length} value={num ? String(num) : ""}
            vacio={!uid ? "— Elegí el ítem —" : tareaDelItem.length ? "— Tarea —" : "Sin tareas asignadas"}
            etiquetaActual={num ? `Tarea ${num}` : ""}
            options={tareaDelItem.map(t => ({ value: String(t.tarea_numero), label: `${t.tarea_numero}. ${t.tarea_nombre}` }))}
            placeholderBuscar="Buscar tarea..."
            onChange={v => updateLocal(idx, `tramo${n}_tarea_numero`, v ? Number(v) : null)}
            className="w-40 border border-gray-300 rounded-lg px-1 py-1 text-xs bg-white" testId={`obra-parte-tarea${n}-${idx}`} />
        </td>
      </>
    );
  };

  return (
    <div className="space-y-4 max-w-6xl mx-auto" data-testid="parte-diario-obra-tab">
      <div className="bg-white rounded-xl border border-gray-200/60 p-4 shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 mb-3">
          <div><label className={labelCls}>Turno</label>
            <SelectTurno turnos={turnos} value={turnoId} onChange={setTurnoId} className={inputCls} testid="obra-parte-turno-select" />
          </div>
          <div><label className={labelCls}>Obra / Proyecto</label>
            {/* Fix FT (2026-10-12): con buscador, como el resto de los desplegables. */}
            <SelectBuscable value={projectId} onChange={setProjectId} vacio="Todas" placeholderBuscar="Buscar obra..."
              options={activeProjects.map(p => ({ value: p._id || p.id, label: p.name }))}
              className={inputCls} testId="obra-parte-project-select" />
          </div>
          <div><label className={labelCls}>Nombre (empleado)</label>
            <SelectorEmpleados workers={filtrarEmpleados(workers, turnoId, projectId)} value={workerIds} onChange={setWorkerIds}
              testid="obra-parte-worker-select" placeholder="Todos los empleados" />
          </div>
          <div><label className={labelCls}>Fecha Inicio</label><input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className={inputCls} data-testid="obra-parte-date-from" /></div>
          <div><label className={labelCls}>Fecha Fin</label><input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className={inputCls} data-testid="obra-parte-date-to" /></div>
        </div>
        <div className="flex gap-2">
          <button onClick={loadRecords} disabled={loading} className="flex-1 bg-[#2E6DA4] hover:bg-[#245A8A] disabled:opacity-50 text-white rounded-xl py-2.5 font-semibold text-sm flex items-center justify-center gap-2" data-testid="obra-parte-load-btn">
            {loading ? <Loader2 size={16} className="animate-spin" /> : null} Cargar Período Habilitado
          </button>
        </div>
        {/* Fix FC (2026-10-06): "El botón de Generar OP de Jornaleros debería estar en Liquidación" (CEN). */}
        <p className="text-[11px] text-gray-400 mt-2">Solo se muestran días ya validados en el Parte Diario Administrativo. Los días validados acá se liquidan en la pestaña «Liquidación».</p>
      </div>

      <IngresosObras datos={ingresos} testid="obra-parte-ingresos" />   {/* Fix GD */}

      {records.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 bg-gray-50/60">
            <label className="flex items-center gap-2 text-xs font-semibold text-gray-600">
              <input type="checkbox" checked={selected.length === records.length && records.length > 0} onChange={toggleSelectAll} data-testid="obra-parte-select-all" />
              {selected.length} / {records.length} seleccionados
            </label>
            <div className="flex gap-2">
              <button onClick={handleValidate} disabled={busy || !selected.length} className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white rounded-lg px-3 py-1.5 text-xs font-bold" data-testid="obra-parte-validate-btn">
                <CheckCircle2 size={13} /> Validar Parte Diario Obra
              </button>
              <button onClick={() => setReopenOpen(true)} disabled={busy || !selected.length} className="flex items-center gap-1.5 bg-amber-100 hover:bg-amber-200 disabled:opacity-40 text-amber-700 rounded-lg px-3 py-1.5 text-xs font-bold" data-testid="obra-parte-reopen-btn">
                <RotateCcw size={13} /> Reabrir
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50"><tr>
                {/* Fix GI (2026-10-15): "en ambos partes diarios y liquidación … congelar las columnas de empleado y fecha" (CEN).
                    La casilla, la fecha y el trabajador quedan fijos a la izquierda al desplazar la tabla. */}
                <th className="px-2 py-2 sticky left-0 z-20 bg-gray-50 w-10 min-w-[40px]"></th>
                <th className="px-2 py-2 text-left sticky left-[40px] z-20 bg-gray-50 w-[110px] min-w-[110px]">Fecha</th>
                <th className="px-2 py-2 text-left sticky left-[150px] z-20 bg-gray-50 w-[230px] min-w-[230px] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.18)]">Trabajador</th>
                <th className="px-2 py-2">Ent1</th><th className="px-2 py-2">Sal1</th><th className="px-2 py-2">Pto 1</th><th className="px-2 py-2">Ítem 1</th><th className="px-2 py-2">Tarea 1</th>
                <th className="px-2 py-2">Ent2</th><th className="px-2 py-2">Sal2</th><th className="px-2 py-2">Pto 2</th><th className="px-2 py-2">Ítem 2</th><th className="px-2 py-2">Tarea 2</th>
                <th className="px-2 py-2 text-amber-700">Descanso</th>
                <th className="px-2 py-2">Horas</th><th className="px-2 py-2">Estado</th><th className="px-2 py-2"></th>
              </tr></thead>
              <tbody>
                {records.map((r, idx) => {
                  const locked = r.obra_status === "validado";
                  return (
                    <tr key={r.id} className="border-t border-gray-100" data-testid={`obra-parte-row-${idx}`}>
                      <td className="px-2 py-2 text-center sticky left-0 z-10 bg-white"><input type="checkbox" checked={selected.includes(r.id)} onChange={() => toggleSelect(r.id)} /></td>
                      <td className="px-2 py-2 font-medium whitespace-nowrap sticky left-[40px] z-10 bg-white">{r.date}</td>
                      <td className="px-2 py-2 whitespace-nowrap sticky left-[150px] z-10 bg-white max-w-[230px] truncate shadow-[2px_0_4px_-2px_rgba(0,0,0,0.18)]" title={r.worker_name}>{r.worker_name}</td>
                      <td className="px-2 py-2 text-center text-gray-500">{r.tramo1_hora_ingreso || "—"}</td>
                      <td className="px-2 py-2 text-center text-gray-500">{r.tramo1_hora_salida || "—"}</td>
                      {celdasAsignacion(r, idx, 1, locked)}
                      <td className="px-2 py-2 text-center text-gray-500">{r.tramo2_hora_ingreso || "—"}</td>
                      <td className="px-2 py-2 text-center text-gray-500">{r.tramo2_hora_salida || "—"}</td>
                      {celdasAsignacion(r, idx, 2, locked)}
                      <td className="px-2 py-2 text-center text-gray-400">{r.descanso || "—"}</td>
                      <td className="px-2 py-2 text-center font-bold text-[#2E6DA4]">{r.horas_trabajadas}h</td>
                      <td className="px-2 py-2 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${locked ? "bg-emerald-100 text-emerald-700" : r.liquidado ? "bg-purple-100 text-purple-700" : "bg-gray-100 text-gray-500"}`}>
                          {r.liquidado ? "Liquidado" : locked ? "Validado" : "Pendiente"}
                        </span>
                      </td>
                      <td className="px-2 py-2">
                        {!locked && <button onClick={() => saveAssign(idx)} disabled={busy} className="text-[#2E6DA4] hover:underline font-semibold whitespace-nowrap" data-testid={`obra-parte-save-${idx}`}>Guardar</button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ReopenMotivoModal open={reopenOpen} onClose={() => setReopenOpen(false)} onConfirm={handleReopen} loading={busy} />
    </div>
  );
}
